import { Children, useState, useEffect } from "react";
import classNames from "classnames";

import { cleanHtml } from "../Utils/htmlStringUtils";
import { useIFrameProcessorEffect } from "./useIFrameProcessorEffect";
import { useContentWithProcessedScriptTags } from "./useContentWithProcessedScriptTags";
import { useCallback } from "react";

const RichText = ({ allowScriptTags = false, ...props }) => {
  const { children, content } = props;
  const className = classNames(["from-user-input", props.className]);
  const [container, setContainer] = useState();

  useIFrameProcessorEffect({ container, content });

  // Clean the `content` prop string from `<script>` tags, and let the hook handle the
  // tags, if allowed, to let the browser execute them.
  const finalContent = useContentWithProcessedScriptTags(content, { allowScriptTags });

  // Ensure that images that are wider than their container are assigned `height: auto` to
  // maintain aspect ratio, as the WYSIWYG editor assigns width and height attributes to
  // all images, and makes the max-width: 100% style create distorted images.
  //
  // This runs on every render, as React seems to re-render the inner HTML on each render,
  // but the `useEffect` would only update on the first render with its dependencies set.
  useEffect(() => {
    if (!container) return;

    const images = container.querySelectorAll("img:not(.h-auto)");

    images.forEach(img => {
      if (img.naturalWidth <= container.clientWidth) return;

      img.classList.add("h-auto");
    });
  });

  // Handle clicks on images to open them in a new tab
  const handleImageClicked = useCallback(event => {
    const { target } = event;

    if (target.tagName !== "IMG") return;

    // Don't intercept the click if the image is inside a link
    // Let the link handle the navigation instead
    if (target.closest("a")) return;

    // Display the image in a new tab
    window.open(target.src, "_blank");
  }, []);

  if (Children.count(children)) {
    return (
      <div className={className} ref={setContainer} onClick={handleImageClicked}>
        {children}
      </div>
    );
  }

  return (
    <div
      className={className}
      ref={setContainer}
      dangerouslySetInnerHTML={{ __html: cleanHtml(finalContent || "") }}
      onClick={handleImageClicked}
    />
  );
};

export default RichText;
